import type { Job } from "@domain/CRM/job";
export interface JobOrderUpdate {
	jobId: string;
	order: number | null;
}

/**
 * Minimal set of `Jobs.Order` writes for a scheduling save. `order` = each job's 0-based index
 * within its shift's ordered `jobIds`; only jobs whose index changed (vs `knownOrderByJobId`) are
 * emitted. A job in `netRemovedJobIds` that is NOT present in any list is cleared (`order: null`);
 * one that reappears in a list keeps its index (it moved shifts, not off the board).
 */
export function computeJobOrderUpdates(
	orderedJobIdLists: readonly (readonly string[])[],
	netRemovedJobIds: Iterable<string>,
	knownOrderByJobId: ReadonlyMap<string, number | undefined>,
): JobOrderUpdate[] {
	const updates: JobOrderUpdate[] = [];
	const assigned = new Set<string>();
	for (const list of orderedJobIdLists) {
		list.forEach((jobId, index) => {
			assigned.add(jobId);
			if (knownOrderByJobId.get(jobId) !== index) updates.push({ jobId, order: index });
		});
	}
	for (const jobId of netRemovedJobIds) {
		if (!assigned.has(jobId)) updates.push({ jobId, order: null });
	}
	return updates;
}

/**
 * Insert `selected` (in selection order) into a shift's ordered `current`, MC-anchor style
 * (ports Mission Control's `prepareOrderedRecords`): if none of `selected` are already on the
 * shift, append; otherwise consolidate the whole selected block at the position of the first
 * already-present id (the "anchor"), preserving the relative order of the non-selected jobs.
 * Result is de-duped.
 */
export function insertJobsOrdered(current: readonly string[], selected: readonly string[]): string[] {
	const uniqueSelected = [...new Set(selected)];
	const selectedSet = new Set(uniqueSelected);
	const alreadyOnShift = uniqueSelected.filter((id) => current.includes(id));
	if (alreadyOnShift.length === 0) {
		return [...current, ...uniqueSelected];
	}
	const anchor = alreadyOnShift[0];
	// keep non-selected jobs and the anchor; drop the other selected-that-were-on-shift
	const anchorIdx = current.filter((id) => !selectedSet.has(id) || id === anchor).indexOf(anchor);
	const result = current.filter((id) => !selectedSet.has(id));
	result.splice(anchorIdx, 0, ...uniqueSelected);
	return result;
}

/**
 * Constrain an intended ordered jobId list to an actual membership: keeps `ordered`'s sequence for
 * ids that are members (de-duped), then appends any members the ordered list missed, in membership
 * iteration order. This is the guard that keeps the ordered staging and the membership bookkeeping
 * from drifting apart — both the save path and the display derive rows through it.
 */
export function reconcileOrderedJobIds(ordered: readonly string[], membership: Iterable<string>): string[] {
	const members = [...new Set(membership)];
	const memberSet = new Set(members);
	const result: string[] = [];
	const placed = new Set<string>();
	for (const id of ordered) {
		if (!memberSet.has(id) || placed.has(id)) continue;
		placed.add(id);
		result.push(id);
	}
	for (const id of members) {
		if (!placed.has(id)) result.push(id);
	}
	return result;
}

/** Move the item at `from` to index `to`, returning a new array. Out-of-range or no-op → copy. */
export function moveJob(list: readonly string[], from: number, to: number): string[] {
	if (from < 0 || from >= list.length || to < 0 || to >= list.length || from === to) return [...list];
	const result = [...list];
	const [moved] = result.splice(from, 1);
	result.splice(to, 0, moved);
	return result;
}

/**
 * Multi-select reorder within one list (the multi-drag-aware reorder from react-beautiful-dnd's
 * multi-drag pattern). Every selected row is pulled out and reinserted as ONE contiguous block at
 * the drop point `to` — a non-consecutive selection consolidates, and the block keeps its members
 * in LIST order (not selection order). Rows outside the selection (e.g. dropoff stops) keep their
 * relative order and reflow around the block. `dragged` is the grabbed row; `to` is the destination
 * index in the full list. Selected ids absent from `list` are ignored; a single traveling row
 * reduces to `moveJob`. The offset excludes `dragged` because dnd's `destination.index` is already
 * computed with the dragged row lifted out.
 */
export function moveSelectedJobs(
	list: readonly string[],
	selected: Iterable<string>,
	dragged: string,
	to: number,
): string[] {
	const selectedSet = new Set(selected);
	const moving = list.filter((id) => selectedSet.has(id)); // list order, members only
	if (moving.length <= 1) return moveJob(list, list.indexOf(dragged), to);
	const offset = moving.reduce((count, id) => {
		if (id === dragged) return count;
		const idx = list.indexOf(id);
		return idx !== -1 && idx < to ? count + 1 : count;
	}, 0);
	const insertAt = to - offset;
	const withRemoved = list.filter((id) => !selectedSet.has(id));
	return [...withRemoved.slice(0, insertAt), ...moving, ...withRemoved.slice(insertAt)];
}

/**
 * Shadow just-written `Jobs.Order` values over possibly-stale in-memory Job records. The
 * scheduled-jobs fetch keys on the job-id SET, so a pure reorder never refetches — after a
 * save, the display would interleave dropoff stops against pre-save orders (user report:
 * a stop saved mid-sequence rendered first). Entries with an explicit `undefined` clear
 * the order. Returns the input map unchanged (same reference) when nothing differs.
 */
export function applyJobOrderOverlay(
	jobsById: ReadonlyMap<string, Job>,
	writtenOrderByJobId: ReadonlyMap<string, number | undefined>,
): ReadonlyMap<string, Job> {
	let out: Map<string, Job> | null = null;
	for (const [jobId, order] of writtenOrderByJobId) {
		const job = jobsById.get(jobId);
		if (!job || job.order === order) continue;
		if (!out) out = new Map(jobsById);
		const patched = Object.assign(Object.create(Object.getPrototypeOf(job)), job, { order }) as Job;
		out.set(jobId, patched);
	}
	return out ?? jobsById;
}
